/**
 * Reads the design tokens back off the document so naive-ui and Tailwind cannot
 * drift apart.
 *
 * naive-ui is CSS-in-JS: it derives hover and pressed shades by doing
 * arithmetic on real colour values, so it cannot consume `var(--token)` the way
 * Tailwind can. The usual workaround is to paste every hex a second time into a
 * themeOverrides object — and then a third time for the discrete message API.
 * The sibling project this was ported from does exactly that, and its three
 * copies have already drifted.
 *
 * Instead we let tokens.css stay the only place a colour is written and read the
 * computed values at boot. getComputedStyle resolves the custom property to the
 * channel triplet, which we wrap back into a colour naive-ui understands.
 */

import type { GlobalThemeOverrides } from 'naive-ui';

/**
 * `--warn` -> `rgb(250, 178, 25)`. Falls back to transparent if absent.
 *
 * COMMAS ARE LOAD-BEARING. tokens.css stores channels space-separated
 * ("250 178 25") because that is what Tailwind's `<alpha-value>` modifier
 * needs. naive-ui parses colour with seemly, which only understands the legacy
 * comma form: handed `rgb(250 178 25)` it THROWS
 *
 *   Error: [seemly/rgba]: Invalid color value rgb(57 135 229)
 *
 * from inside changeColor(), which runs in a computed while naive-ui derives
 * its hover and pressed shades. A throw there takes down the whole view that
 * mounted the component, not just the component — which is how one unparsed
 * colour turns a screen blank. Same reason alpha is emitted as `rgba(r, g, b,
 * a)` rather than the modern `rgb(r g b / a)`.
 */
export function token(name: string, alpha?: number): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();
  if (!raw) {
    if (import.meta.env.DEV) console.warn(`[tokens] --${name} is not defined`);
    return 'transparent';
  }
  const channels = raw.split(/[\s,]+/).filter(Boolean).join(', ');
  return alpha === undefined ? `rgb(${channels})` : `rgba(${channels}, ${alpha})`;
}

/** A plain length token such as `--radius-card`, returned as written. */
export function length(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();
}

export const FONT_SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif';
export const FONT_MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';

/**
 * naive-ui's theme, derived from the same tokens.
 *
 * Call this after the stylesheets have loaded — it reads the DOM. Anything
 * naive-ui needs a *shade* of (hover, pressed) is taken from a real token
 * rather than computed, so the palette stays exactly what the design system
 * specifies.
 */
export function naiveThemeOverrides(): GlobalThemeOverrides {
  const common = {
    primaryColor: token('a1'),
    primaryColorHover: token('link'),
    primaryColorPressed: token('link-hover'),
    primaryColorSuppl: token('a1'),

    successColor: token('good'),
    warningColor: token('warn'),
    errorColor: token('crit'),
    infoColor: token('a1'),

    bodyColor: token('plane'),
    cardColor: token('surface'),
    modalColor: token('surface'),
    popoverColor: token('surface'),
    tableColor: token('surface'),
    tableHeaderColor: token('surface'),
    inputColor: token('inset'),
    inputColorDisabled: token('inset'),

    borderColor: token('line'),
    dividerColor: token('line'),

    textColorBase: token('ink'),
    textColor1: token('ink'),
    textColor2: token('ink2'),
    textColor3: token('muted'),
    textColorDisabled: token('muted'),
    placeholderColor: token('muted'),

    borderRadius: length('radius-control'),
    borderRadiusSmall: length('radius-control'),

    fontFamily: FONT_SANS,
    fontFamilyMono: FONT_MONO,
    fontSize: '13px',
  };

  return {
    common,
    DataTable: {
      // the design system's table: a hairline between rows, 9px cell padding,
      // and a hover that is a ground change rather than a border
      thColor: token('surface'),
      tdColor: token('surface'),
      tdColorHover: token('hover'),
      borderColor: token('line'),
      thTextColor: token('muted'),
      tdTextColor: token('ink2'),
      thFontWeight: '600',
    },
    Button: {
      textColorGhost: token('ink2'),
      borderHover: `1px solid ${token('muted')}`,
      borderPressed: `1px solid ${token('muted')}`,
    },
    Input: {
      border: `1px solid ${token('rule')}`,
      borderHover: `1px solid ${token('muted')}`,
      borderFocus: `1px solid ${token('muted')}`,
      boxShadowFocus: `0 0 0 1px ${token('muted')}`,
      caretColor: token('ink'),
    },
    Select: {
      peers: {
        InternalSelection: {
          border: `1px solid ${token('rule')}`,
          borderHover: `1px solid ${token('muted')}`,
          borderFocus: `1px solid ${token('muted')}`,
          boxShadowFocus: `0 0 0 1px ${token('muted')}`,
        },
      },
    },
  };
}
